<template>
    <div>
        <v-container>
            <v-row class="mb-2">
                <v-col cols="3" class="mt-4">
                    <v-btn :href="downloadUrl">
                        <v-icon>mdi-download</v-icon>
                        Download
                    </v-btn>
                </v-col>
                <v-col cols="3">
                    <v-switch v-model="showAgeGroups" label="Show Age Groups" />
                </v-col>
                <v-col cols="3">
                    <v-switch v-model="summarizeCounts" label="Summarize by Count" />
                </v-col>
                <v-col cols="3" class="text-right">
                    <v-text-field
                        v-model="search"
                        append-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                    ></v-text-field>
                </v-col>
            </v-row>
        </v-container>

        <v-data-table
            v-if="summarizeCounts"
            :headers="summaryHeaders"
            :items="qualifiers"
            :loading="loading"
            :items-per-page="-1"
            dense
            disable-sort
            hide-default-footer
        />

        <v-data-table
            v-else
            :headers="headers"
            :items="qualifiers"
            :loading="loading"
            :search="search"
            :custom-filter="searchFilter"
            :items-per-page="-1"
            dense
            disable-sort
            hide-default-footer
        >
            <template v-if="false" #[`header.tra_level`]>
                <v-chip color="blue" class="lighten-1" dark small>Level</v-chip>
            </template>

            <template v-if="false" #[`header.dmt_level`]>
                <v-chip color="green" class="lighten-1" dark small>Level</v-chip>
            </template>

            <template v-if="false" #[`header.tum_level`]>
                <v-chip color="red" class="lighten-1" dark small>Level</v-chip>
            </template>

            <template #[`item.name`]="{ item }">
                <inertia-link :href="`/athletes/${item.athlete.id}`">{{ item.athlete.name }}</inertia-link>
            </template>

            <template #[`item.club`]="{ item }">
                <inertia-link :href="`/clubs/${item.club.id}`">{{ item.club.name }}</inertia-link>
            </template>

            <template #[`item.gender`]="{ item }">
                {{ item.athlete.gender.toUpperCase() }}
            </template>

            <template #[`item.tra_level`]="{ item }">
                {{ get(item, 'trampoline.result.level') }}
            </template>

            <template #[`item.dmt_level`]="{ item }">
                {{ get(item, 'doubleMini.result.level') }}
            </template>

            <template #[`item.tum_level`]="{ item }">
                {{ get(item, 'tumbling.result.level') }}
            </template>

            <template #[`item.tra_age_group`]="{ item }">
                {{ get(item, 'trampoline.result.age_group') }}
            </template>

            <template #[`item.dmt_age_group`]="{ item }">
                {{ get(item, 'doubleMini.result.age_group') }}
            </template>

            <template #[`item.tum_age_group`]="{ item }">
                {{ get(item, 'tumbling.result.age_group') }}
            </template>

            <template #[`item.tra_qualified`]="{ item }">
                <qualifier-check
                    v-if="item.trampoline"
                    :report="item"
                    color="blue"
                    event="trampoline"
                    verification="qualified"
                    verification-mark="qualification.total_score"
                />
            </template>

            <template #[`item.dmt_qualified`]="{ item }">
                <qualifier-check
                    v-if="item.doubleMini"
                    :report="item"
                    color="green"
                    event="doubleMini"
                    verification="qualified"
                    verification-mark="qualification.total_score"
                />
            </template>

            <template #[`item.tum_qualified`]="{ item }">
                <qualifier-check
                    v-if="item.tumbling"
                    :report="item"
                    color="red"
                    event="tumbling"
                    verification="qualified"
                    verification-mark="qualification.total_score"
                />
            </template>

            <template #[`item.tra_mobilized`]="{ item }">
                <qualifier-check
                    v-if="item.trampoline"
                    :report="item"
                    color="blue"
                    event="trampoline"
                    verification="mobilized"
                    verification-mark="mobility.total_score"
                >
                    Mobility Score
                </qualifier-check>
            </template>

            <template #[`item.dmt_mobilized`]="{ item }">
                <qualifier-check
                    v-if="item.trampoline"
                    :report="item"
                    color="green"
                    event="doubleMini"
                    verification="mobilized"
                    verification-mark="mobility.total_score"
                >
                    Mobility Score
                </qualifier-check>
            </template>

            <template #[`item.tum_mobilized`]="{ item }">
                <qualifier-check
                    v-if="item.tumbling"
                    :report="item"
                    color="red"
                    event="tumbling"
                    verification="mobilized"
                    verification-mark="mobility.total_score"
                >
                    Mobility Score
                </qualifier-check>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import get from 'lodash/get.js';
import { VBtn } from 'vuetify/lib';
import Meet from '@/Models/Meet.js';
import QualifierCheck from '@/Components/Qualifiers/QualifierCheck.vue';
import QueryBuilder from '@/query-builder.js';

export default {
    name: 'Qualifiers',

    components: {
        QualifierCheck,
        VBtn,
    },

    props: {
        identifier: {
            type: String,
            required: true,
        },

        id: {
            type: Number,
            required: true,
        },

        meet: {
            type: Meet,
            required: true,
        },
    },

    data: () => ({
        loading: false,
        search: '',
        qualifiers: [],
        showAgeGroups: false,
        summarizeCounts: false,

        headers: [],
        summaryHeaders: [
            { text: 'Event', sortable: true, value: 'event' },
            { text: 'Level', sortable: true, value: 'level' },
            { text: 'Gender', sortable: true, value: 'gender' },
            { text: 'Age Group', sortable: true, value: 'age_group' },
            { text: 'Total', sortable: true, value: 'total_count' },
            { text: 'Qualified', sortable: true, value: 'qualified_count' },
            { text: 'Non-Qualified', sortable: true, value: 'non_qualified_count' },
            { text: 'Percent', sortable: true, value: 'percent' },
        ],
    }),

    computed: {
        query() {
            return new QueryBuilder('qualifiers').raw({
                identifier: 'meet',
                id: this.id,
                summarize: this.summarizeCounts ? '1' : '0',
            });
        },

        downloadUrl() {
            return this.query.raw({ download: '1' }).toUrl();
        },
    },

    watch: {
        showAgeGroups() {
            this.loadHeaders();
        },

        summarizeCounts() {
            this.loadQualifiers();
        },
    },

    created() {
        this.loadHeaders();

        this.loadQualifiers();
    },

    methods: {
        get,

        loadHeaders() {
            this.headers = [
                {
                    text: 'Name',
                    sortable: true,
                    value: 'name',
                },
                {
                    text: 'Club',
                    sortable: true,
                    value: 'club',
                },
                {
                    text: 'Gender',
                    sortable: true,
                    value: 'gender',
                },
                {
                    text: 'TRA Level',
                    sortable: true,
                    value: 'tra_level',
                },
            ];

            if (this.showAgeGroups) {
                this.headers = [
                    ...this.headers,
                    ...[
                        {
                            text: 'TRA Age Group',
                            sortable: true,
                            value: 'tra_age_group',
                        },
                    ],
                ];
            }

            this.headers = [
                ...this.headers,
                {
                    text: 'DMT Level',
                    sortable: true,
                    value: 'dmt_level',
                },
            ];

            if (this.showAgeGroups) {
                this.headers = [
                    ...this.headers,
                    ...[
                        {
                            text: 'DMT Age Group',
                            sortable: true,
                            value: 'dmt_age_group',
                        },
                    ],
                ];
            }

            this.headers = [
                ...this.headers,
                {
                    text: 'TUM Level',
                    sortable: true,
                    value: 'tum_level',
                },
            ];

            if (this.showAgeGroups) {
                this.headers = [
                    ...this.headers,
                    ...[
                        {
                            text: 'TUM Age Group',
                            sortable: true,
                            value: 'tum_age_group',
                        },
                    ],
                ];
            }

            if (this.meet.jo_qualification_meet || this.meet.elite_qualification_meet) {
                this.headers = [
                    ...this.headers,
                    ...[
                        {
                            text: 'TRA Qualified',
                            sortable: true,
                            value: 'tra_qualified',
                        },
                        {
                            text: 'DMT Qualified',
                            sortable: true,
                            value: 'dmt_qualified',
                        },
                        {
                            text: 'TUM Qualified',
                            sortable: true,
                            value: 'tum_qualified',
                        },
                    ],
                ];
            }

            this.headers = [
                ...this.headers,
                ...[
                    {
                        text: 'TRA Mobilized',
                        sortable: true,
                        value: 'tra_mobilized',
                    },
                    {
                        text: 'DMT Mobilized',
                        sortable: true,
                        value: 'dmt_mobilized',
                    },
                    {
                        text: 'TUM Mobilized',
                        sortable: true,
                        value: 'tum_mobilized',
                    },
                ],
            ];
        },

        async loadQualifiers() {
            this.loading = true;

            const { data } = await axios.get(this.query.toUrl());

            this.qualifiers = data.data;

            this.loading = false;
        },

        searchFilter(value, search, item) {
            return (
                item.athlete.name.toLowerCase().includes(search.toLowerCase()) ||
                item.club.name.toLowerCase().includes(search.toLowerCase())
            );
        },
    },
};
</script>
